import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IrpAppService from '../../../../application/platform/scp/irp/irp-app-service';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import EntityAppService from '../../../../application/platform/scp/entity/entitiy-app-service';
import { ACTION_TYPES, useIrpCreateEditDispatch, useIrpCreateEditState } from './irp-create-edit-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IIrpAddEditRequest } from '../../../../model/scp/irp/request/irp-add-edit-request';
import { IIrpResponse } from '../../../../model/scp/irp/response/irp-response';
import { useBaseData } from '../../../../application/context/base-data-context';
import { sanitizeText } from '../../../../utils/text';
import { hasErrors } from '../../../../utils/validation';
import { validateIrp } from './irp-validation';
import { countDraftItems } from '../../shared/scp-utils';

const LIST_ROUTE = '/intencao-registro-preco';
const IRP_DOCUMENT_TYPE = 6;

const FIELD_BY_INPUT: Record<string, keyof IIrpAddEditRequest> = {
    'select-legal-support': 'legalSupportId',
    'select-type-calling-instrument': 'typeCallingInstrumentId',
    'select-modality': 'modalityId',
    'select-type-document': 'documentTypeId',
    'select-dispute-mode': 'disputeModeId',
    'select-managing-unit': 'codManagingUnit',
    'input-year-irp': 'yearIrp',
    'input-number-irp': 'numberIrp',
    'input-process-number': 'processNumber',
    'input-object': 'objectIrp',
    'input-title': 'title',
    'input-complementary-information': 'complementaryInformation',
    'input-url-system-origin': 'urlSystemOrigin',
    'input-url-electronic-process': 'urlElectronicProcess',
    'input-justification': 'justification',
    'select-client': 'clientId',
};

const SANITIZED_INPUTS = new Set(['input-object', 'input-title', 'input-complementary-information']);

function toFormRequest(data: IIrpResponse): IIrpAddEditRequest {
    return {
        id: data.id,
        clientId: data.clientId,
        codManagingUnit: data.codManagingUnit || '',
        typeCallingInstrumentId: data.typeCallingInstrumentId,
        modalityId: data.modalityId,
        disputeModeId: data.disputeModeId,
        legalSupportId: data.legalSupportId,
        numberIrp: data.numberIrp || '',
        yearIrp: data.yearIrp,
        processNumber: data.processNumber,
        objectIrp: data.objectIrp || '',
        complementaryInformation: data.complementaryInformation || '',
        indPossibilityAdhesion: data.indPossibilityAdhesion,
        finalInterestManifestationDate: data.finalInterestManifestationDate,
        urlSystemOrigin: data.urlSystemOrigin,
        urlElectronicProcess: data.urlElectronicProcess,
    };
}

export const useIrpCreateEditController = () => {
    const navigate = useNavigate();
    const { showError, showSuccess, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useIrpCreateEditDispatch();
    const state = useIrpCreateEditState();
    const baseData = useBaseData();
    const isEdit = state.irpId !== undefined;

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!baseData.loaded) return;
        dispatch({ type: ACTION_TYPES.setInstrumentSummons, payload: baseData.instrumentSummons });
        dispatch({ type: ACTION_TYPES.setModality, payload: baseData.modality });
        dispatch({ type: ACTION_TYPES.setDisputeMode, payload: baseData.disputeMode });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseData.loaded]);

    async function loadInitialData() {
        try {
            await withLoading(async () => {
                const [legalSupport, typeDocument, unidEntity] = await Promise.all([
                    BaseAppService.listLegalSupport(),
                    BaseAppService.listTypeDocument(IRP_DOCUMENT_TYPE),
                    EntityAppService.listUnidEntity(),
                ]);
                dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
                dispatch({ type: ACTION_TYPES.setTypeDocument, payload: typeDocument });
                dispatch({ type: ACTION_TYPES.setUnidEntity, payload: unidEntity });

                if (state.irpId === undefined) return;
                const data = await IrpAppService.getIrp(state.irpId);
                // O amparo legal depende do instrumento e da modalidade: recarrega antes de preencher o formulário
                if (data.typeCallingInstrumentId || data.modalityId) {
                    const filteredLegalSupport = await BaseAppService.listLegalSupport(data.typeCallingInstrumentId, data.modalityId);
                    dispatch({ type: ACTION_TYPES.setLegalSupport, payload: filteredLegalSupport });
                }
                dispatch({ type: ACTION_TYPES.setIrpRequest, payload: toFormRequest(data) });
            });
        } catch (error: any) {
            showError('Não foi possível carregar os dados da intenção de registro de preço', error);
        }
    }

    function handleTabChange(_event: any, newTabIndex: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex1, payload: newTabIndex });
    }

    function handleOnChange(e: any) {
        const { name, value, checked } = e.target;
        if (name === 'check-ind-possibility-adhesion') {
            dispatch({ type: ACTION_TYPES.setIrpRequest, payload: { indPossibilityAdhesion: checked } });
            return;
        }

        const field = FIELD_BY_INPUT[name];
        if (!field) return;
        dispatch({ type: ACTION_TYPES.setIrpRequest, payload: { [field]: SANITIZED_INPUTS.has(name) ? sanitizeText(value) : value } });

        if (name === 'select-type-calling-instrument') reloadLegalSupport(value, state.irpRequest.modalityId);
        if (name === 'select-modality') reloadLegalSupport(state.irpRequest.typeCallingInstrumentId, value);
    }

    async function reloadLegalSupport(instrumentSummonsId?: number | null, modalityId?: number | null) {
        try {
            const legalSupport = await BaseAppService.listLegalSupport(instrumentSummonsId ?? undefined, modalityId ?? undefined);
            dispatch({ type: ACTION_TYPES.setLegalSupport, payload: legalSupport });
            // A seleção anterior pode não existir na nova lista
            dispatch({ type: ACTION_TYPES.setIrpRequest, payload: { legalSupportId: 0 } });
        } catch (error: any) {
            showError('Não foi possível recarregar o amparo legal', error);
        }
    }

    function handleDateChange(field: keyof IIrpAddEditRequest, value: string | null) {
        dispatch({ type: ACTION_TYPES.setIrpRequest, payload: { [field]: value } });
    }

    function setFileUpload(file: File) {
        dispatch({ type: ACTION_TYPES.setFile, payload: file });
    }

    function setItemsIrpJson(json: string) {
        dispatch({ type: ACTION_TYPES.setItemsIrpJson, payload: json });
    }

    function navigateToList() {
        navigate(LIST_ROUTE);
    }

    async function persist() {
        if (state.irpId !== undefined) {
            await IrpAppService.updateIrp(state.irpId, state.irpRequest);
            showSuccess('Intenção de registro de preço atualizada com sucesso!');
            navigateToList();
            return;
        }
        const created = await IrpAppService.addIrp(state.irpRequest, state.file!, state.itemsIrpJson);
        showSuccess('Intenção de registro de preço adicionada com sucesso!');
        // Abre a edição do registro criado para que os arquivos possam ser anexados
        navigate(created?.id ? `${LIST_ROUTE}/publicar/${created.id}` : LIST_ROUTE, { replace: true });
    }

    async function addOrEditIrp() {
        if (isSaving) return;
        const errors = validateIrp(state.irpRequest, { isEdit, hasFile: !!state.file });
        if (hasErrors(errors)) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            showError('Preencha todos os campos obrigatórios');
            return;
        }
        if (!isEdit && countDraftItems(state.itemsIrpJson) === 0) {
            showError('É necessário adicionar pelo menos um item à intenção de registro de preço');
            return;
        }

        setIsSaving(true);
        try {
            await withLoading(persist);
        } catch (error: any) {
            showError('Não foi possível salvar a intenção de registro de preço', error);
        } finally {
            setIsSaving(false);
        }
    }

    return {
        isEdit,
        isSaving,
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsIrpJson,
        addOrEditIrp,
        navigateToList,
    };
};
